/**
 * File: agentJob.ts
 * Description: Types for the navigation-resilient agent-job tracker. A
 *   descriptor is the client-side record of a long-running background agent
 *   run (ADR generation, section generation, C4 diagram, etc.). It is tracked
 *   above the router and persisted to localStorage so a run survives page
 *   navigation and full reloads.
 */

import { AsyncJobStatus } from './asyncJob';

/** Lifecycle of a tracked job; mirrors the backend job statuses. */
export type JobLifecycle = AsyncJobStatus;

/**
 * Client-side record of a tracked background agent run.
 *
 * `target` is the domain identifier a page uses to ask "is *my* thing
 * running?" (for example an ADR id, an architecture section, or a diagram
 * tab). See {@link ./../services/agentJobTargets}.
 */
export interface AgentJobDescriptor<TResult = unknown> {
  /** The backend AsyncJob id (UUID) — the dedupe key. */
  jobId: string;
  /** Owning project id. */
  projectId: number;
  /** Logical job category (mirrors backend JOB_TYPE_*). */
  jobType: string;
  /** Domain target this run produces output for (page join key). */
  target?: string;
  /** Human-readable label for logging/diagnostics. */
  label: string;
  /** Epoch ms when tracking started. */
  startedAt: number;
  /** Current lifecycle status. */
  status: JobLifecycle;
  /** Cached result payload once the job has succeeded. */
  result?: TResult;
  /** Error message once the job has failed. */
  error?: string;
  /** Epoch ms when the job reached a terminal state. */
  finishedAt?: number;
}

/** True when a status is terminal (no further transitions expected). */
export const isTerminalStatus = (status: JobLifecycle): boolean =>
  status === 'succeeded' || status === 'failed';
